import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { DeviceReadiness } from "../call/DeviceReadiness";
import type { MediaDevicePreferences } from "../../services/mediaDevicePreferences";
import {
  cancelSession, clearStoredSession, forgetInvite, getSession, markReady, readInviteToken, readStoredSession,
  redeemInvitation, rememberInviteToken
} from "../../services/publicSession";
import { useI18n } from "../../i18n/localization";
import { createGuestReady, guestDevicesReady } from "./guestReady";
import {
  initialPublicFlow, inviteTokenFromHash, isInvitationGone, isSessionGone, publicErrorKey, publicFlowReducer
} from "./publicFlow";
import { PublicCallStage } from "./PublicCallStage";
import { PrimaryButton, StepCard, muted } from "./PublicShell";
import { usePublicLiveSync } from "./usePublicLiveSync";

// Guest link ("/join#<token>"): no registration/OTP — the one-time invitation admits the guest
// to that invitation only. Lands directly in device readiness; a refresh reuses the stored guest
// session, or re-redeems the same link (the server keeps one invitation and one call).
// Readiness continues AUTOMATICALLY once camera and microphone both work (no guest Ready button);
// after an explicit "Leave" the guest rejoins with the manual Ready button instead, so leaving
// is not undone by itself. The initiator flow (PublicEntry) keeps its manual Ready.
export function GuestJoin() {
  const { t } = useI18n();
  const [state, dispatch] = useReducer(publicFlowReducer, initialPublicFlow);
  const [token, setToken] = useState<string | null>(() => readStoredSession("guest")?.token ?? null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [unavailable, setUnavailable] = useState(false);
  const [autoJoin, setAutoJoin] = useState(true);

  const closeInvalid = useCallback(() => {
    clearStoredSession("guest");
    forgetInvite();
    setToken(null);
    dispatch({ type: "invalidInvite" });
  }, []);

  /** Redeem the stored link: the new guest session token, "closed" (closed screen shown) or null (unavailable). */
  const redeem = useCallback(async (): Promise<string | "closed" | null> => {
    const invite = readInviteToken();
    if (!invite) { closeInvalid(); return "closed"; }
    try {
      const r = await redeemInvitation(invite);
      tokenRef.current = r.session.token;
      setToken(r.session.token);
      dispatch({ type: "view", view: r.state });
      return r.session.token;
    } catch (e) {
      if (isInvitationGone(e) || isSessionGone(e)) { closeInvalid(); return "closed"; }
      setUnavailable(true);
      return null;
    }
  }, [closeInvalid]);

  // One controller for the page: in-flight guard + bounded session re-issue (see guestReady.ts).
  const redeemRef = useRef(redeem);
  redeemRef.current = redeem;
  const [guestReady] = useState(() => createGuestReady({
    token: () => tokenRef.current,
    markReady,
    getSession,
    reissue: () => redeemRef.current(),
    dispatch
  }));

  const start = useCallback(async () => {
    setUnavailable(false);
    // The link token travels in the URL fragment (never sent to the server or proxy logs);
    // keep it for this tab and remove it from the address bar / history.
    const fromHash = inviteTokenFromHash(window.location.hash);
    if (fromHash) {
      rememberInviteToken(fromHash);
      clearStoredSession("guest"); // a new link replaces any earlier guest session in this tab
      window.history.replaceState(null, "", window.location.pathname);
    }
    const stored = fromHash ? null : readStoredSession("guest");
    if (stored) {
      try {
        dispatch({ type: "view", view: await getSession(stored.token) });
        tokenRef.current = stored.token;
        setToken(stored.token);
        return;
      } catch (e) {
        if (!isSessionGone(e)) { setUnavailable(true); return; }
        clearStoredSession("guest");
      }
    }
    await redeem();
  }, [redeem]);
  // Once per mount (StrictMode re-runs effects in development; a second redeem would only
  // supersede the first guest session).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start();
  }, [start]);

  // A superseded/expired guest session falls back to re-redeeming the same link.
  const onSessionGone = useCallback(() => {
    clearStoredSession("guest");
    tokenRef.current = null;
    setToken(null);
    void redeem();
  }, [redeem]);

  const { endCall } = usePublicLiveSync({ token, screen: state.screen, dispatch, onSessionGone });

  const act = async (work: () => Promise<void>) => {
    dispatch({ type: "error", error: null });
    try {
      await work();
    } catch (e) {
      if (isSessionGone(e)) onSessionGone();
      else if (isInvitationGone(e) && token) dispatch({ type: "view", view: await getSession(token).catch(() => state.view!) });
      else dispatch({ type: "error", error: publicErrorKey(e) });
    }
  };

  const screen = state.screen;
  if (screen.kind === "loading") {
    return (
      <StepCard title={t.pub.guestTitleInvited} labelledBy="pub-guest-loading">
        <p role="status" style={muted}>{unavailable ? t.pub.errors.unavailable : t.pub.loading}</p>
        {unavailable && <PrimaryButton type="button" onClick={() => void start()}>{t.pub.continue}</PrimaryButton>}
      </StepCard>
    );
  }

  if (screen.kind === "readiness") {
    return (
      <GuestReadinessStage autoJoin={autoJoin} otherPartyName={state.view?.otherPartyName ?? null}
        onReady={(prefs) => void guestDevicesReady(prefs, dispatch, guestReady)} />
    );
  }

  return (
    <PublicCallStage screen={screen} view={state.view} prefs={state.prefs!}
      error={state.error ? t.pub.errors[state.error] : null}
      onRetry={() => void guestReady.send()}
      onPrefs={(prefs) => dispatch({ type: "devicesReady", prefs })}
      onCancel={() => void act(async () => {
        if (token) dispatch({ type: "view", view: await cancelSession(token) });
        setAutoJoin(false); // an explicit leave is not undone automatically: rejoin uses the Ready button
        dispatch({ type: "devicesReset" });
      })}
      onEnd={(callId) => void endCall(callId)} />
  );
}

/**
 * Guest readiness stage. `autoJoin` (default path) hides the Ready button and continues once
 * camera and microphone both work; `onReady` fires at most once per mounted stage.
 */
export function GuestReadinessStage({ autoJoin, otherPartyName, onReady }: {
  autoJoin: boolean;
  otherPartyName: string | null;
  onReady: (prefs: MediaDevicePreferences) => void;
}) {
  const { t } = useI18n();
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div role="status" style={{ background: "#eef3fb", borderBottom: "1px solid #d6deea", padding: "10px 16px", fontSize: 14, color: "#14305f" }}>
        <strong>{t.pub.guestTitleInvited}</strong>
        {otherPartyName && <> · {t.pub.invitedBy} <strong>{otherPartyName}</strong></>}
        <div style={{ color: "#5b6b82", fontSize: 13, marginTop: 2 }}>{autoJoin ? t.pub.guestText2 : t.pub.guestTextManual}</div>
      </div>
      <DeviceReadiness role="field" continueLabel={t.pub.readyAction} autoContinue={autoJoin} onReady={onReady} />
    </div>
  );
}
